import React from "react";
import { Card } from "antd";
import {
    AppstoreOutlined,
    DashboardOutlined,
    EnvironmentOutlined,
    TagsOutlined
} from "@ant-design/icons";
import "./style.css";

/**
 * FleetOverviewCard
 *
 * Consolidates the fleet's asset-count and device/sensor-coverage figures into a
 * single, well-structured card (replacing the previous row of separate KPI tiles).
 *
 * All values are supplied by the parent Dashboard from real, already-computed data.
 * This component performs NO data fetching, guessing or derivation of its own — it
 * only formats and lays out what it is given. Missing / non-numeric values render as
 * an em dash ("—") rather than a fabricated zero, so a genuine 0 stays distinct from
 * "not available".
 */

const fmt = (n) => {
    if (n === null || n === undefined || (typeof n === "number" && isNaN(n))) return "—";
    return Number(n).toLocaleString();
};

const FleetOverviewCard = (props) => {
    const {
        loading = false,
        totalAssets = 0,
        countable = 0,
        unique = 0,
        newThisMonth = 0,
        disposedThisMonth = 0,
        inTransit = 0,
        showCoverage = false,
        exclTagSensor = 0,
        fuelSensor = 0,
        gps = 0,
        ble = 0
    } = props;

    const coverage = [
        { key: "excl", label: "Excl. Tag / Sensor", value: exclTagSensor, cls: "fo-i-neutral", icon: <AppstoreOutlined /> },
        { key: "fuel", label: "Fuel Sensor", value: fuelSensor, cls: "fo-i-teal", icon: <DashboardOutlined /> },
        { key: "gps", label: "GPS Active", value: gps, cls: "fo-i-accent", icon: <EnvironmentOutlined /> },
        { key: "ble", label: "BLE Tagged", value: ble, cls: "fo-i-violet", icon: <TagsOutlined /> }
    ];

    const hasNew = newThisMonth > 0;
    const hasDisposed = disposedThisMonth > 0;

    // Device & Sensor Coverage ratio.
    //   numerator   = Fuel Sensor + GPS + BLE  (assets carrying each device type)
    //   denominator = total assets EXCLUDING tag/sensor devices themselves
    // i.e. what share of real (non-device) assets are instrumented. The device
    // records are excluded from the base because they are not assets being tracked.
    // NOTE: the numerator is a straight sum of the three device-type counts, so an
    // asset carrying more than one device type is counted once per device — this is
    // the formula requested by the business, not a distinct-asset count.
    const covered = (Number(gps) || 0) + (Number(ble) || 0);
    const pct = (typeof exclTagSensor === "number" && exclTagSensor > 0)
        ? Math.round((covered / exclTagSensor) * 1000) / 10
        : null;
    const pctWidth = pct == null ? 0 : Math.max(0, Math.min(100, pct));
    const pctLabel = pct == null ? "—" : `${pct}%`;

    return (
        <Card loading={loading} className="gx-card-widget fo-card" bordered={false}>
            <div className="fo-inner">
                <div className="fo-total">
                    <div className="fo-eyebrow">Total Assets</div>
                    <div className="fo-total-main">
                        <img className="fo-total-ico" src="/icons/fixed-asset.png" alt="" />
                        <div className="fo-big">{fmt(totalAssets)}</div>
                    </div>
                    <div className="fo-trend">
                        {hasNew && <span className="fo-up">↑ +{newThisMonth} this month</span>}
                        {hasNew && hasDisposed && <span className="fo-sep">·</span>}
                        {hasDisposed && <span className="fo-down">↓ {disposedThisMonth} disposed</span>}
                        {!hasNew && !hasDisposed && <span className="fo-muted">No change this month</span>}
                    </div>
                    <div className="fo-split">
                        <div className="fo-cell"><b>{fmt(countable)}</b><span>Countable</span></div>
                        <div className="fo-cell"><b>{fmt(unique)}</b><span>Unique</span></div>
                        {/* Included in the total: assets on an open ATA have no location until received. */}
                        <div className="fo-cell" title="Included in the total: on an open ATA, not yet received at their destination">
                            <b>{fmt(inTransit)}</b><span>In transit</span>
                        </div>
                    </div>
                </div>

                {showCoverage && (
                    <React.Fragment>
                        <div className="fo-div" />
                        <div className="fo-cov">
                            <div className="fo-covhead">
                                <div className="fo-eyebrow">Device &amp; Sensor Coverage</div>
                                <div className="fo-tracked">
                                    <b>{fmt(covered)}</b> of {fmt(exclTagSensor)} assets covered
                                </div>
                            </div>
                            <div className="fo-covbar">
                                <div className="fo-covbar-track">
                                    <div className="fo-covbar-fill" style={{ width: `${pctWidth}%` }} />
                                </div>
                                <span className="fo-covbar-pct">{pctLabel}</span>
                            </div>
                            <div className="fo-covgrid">
                                {coverage.map((c) => (
                                    <div className="fo-covitem" key={c.key}>
                                        <span className={`fo-ico ${c.cls}`}>{c.icon}</span>
                                        <span className="fo-covx">
                                            <b>{fmt(c.value)}</b>
                                            <small>{c.label}</small>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </React.Fragment>
                )}
            </div>
        </Card>
    );
};

export default FleetOverviewCard;
